import React, { ReactElement } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { SortDir, SortState } from './patientstable.store';

type SortInfo = {
  iconClass: ReactElement;
  ariaSort: 'ascending' | 'descending' | 'none';
}

type SortableTableHeaderProps<K extends string> = {
  label: string;
  sortKey: K;
  sort: SortState;
  onSort: (key: K) => void;
  className?: string;
  style?: React.CSSProperties;
}

const getSortInfo = (isActive: boolean, dir: SortDir | null): SortInfo => {
  if (!isActive) {
    return { iconClass: (<ArrowUpDown size={14} />), ariaSort: 'none' };
  }

  if (dir === 'asc') {
    return { iconClass: (<ArrowDown size={14} />), ariaSort: 'ascending' };
  }

  return { iconClass: (<ArrowUp size={14} />), ariaSort: 'descending' };
};

const SortableTableHeader = <K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className,
  style,
}: SortableTableHeaderProps<K>): ReactElement => {
  const isActive: boolean = sort?.key === sortKey;
  const dir: SortDir | null = isActive ? sort!.dir : null;

  const sortInfo: SortInfo = getSortInfo(isActive, dir);

  const onKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort(sortKey);
    }
  };

  return (
    <th
      role="button"
      tabIndex={0}
      aria-sort={sortInfo.ariaSort}
      onClick={() => onSort(sortKey)}
      onKeyDown={onKeyDown}
      className={className}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        ...style
      }}
    >
      <div className="d-flex align-items-center justify-content-start gap-2">
        <span className="text-nowrap">{label}</span>
        <span
          className={isActive ? 'text-body' : 'text-muted'}
          style={{
            width: 18,
            textAlign: 'right',
            lineHeight: 1
          }}
        >
          {sortInfo.iconClass}
        </span>
      </div>
    </th>
  );
};

export default SortableTableHeader;